import { Flex } from '@chakra-ui/react';
import { LineChart, TrendingDown, TrendingUp } from 'lucide-react';
import React, { useMemo } from 'react';
import { OktonChart as Chart } from '@okton/platform-ui/charts';
import {
  Agrupamento,
  DadosEvolucao,
} from '../../../services/hooks/useDashboard';
import formatValue from '../../../utils/formatValue';
import { rotuloEixo } from '../formatacao';
import CargaGrafico from './CargaGrafico';
import ChartInfo from './ChartInfo';
import { useCoresDashboard } from './cores';

interface Props {
  dados?: DadosEvolucao;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  onSelecionarPeriodo: (periodo: string, agrupamento: Agrupamento) => void;
  atualizando?: boolean;
}

const TITULO = 'Entradas x Saídas';
const ALTURA = '420px';

const EntradasSaidasChart: React.FC<Props> = ({
  dados,
  isLoading,
  isError,
  error,
  onRetry,
  onSelecionarPeriodo,
  atualizando,
}) => {
  const cores = useCoresDashboard();
  const agrupamento = dados?.agrupamento ?? 'dia';
  // Um dia só: as barras por hora do lançamento. Num dia em que nenhum
  // lançamento tem hora (extrato de antes da hora existir), um ponto único
  // daria duas barras: cada lançamento vira uma barra, na ordem em que
  // entrou, com o número na frente distinguindo os de mesma descrição.
  const horas = dados?.temhora ? dados.horas : null;
  const lancamentos = horas ? null : dados?.lancamentos;

  // Memorizada para um refetch com os mesmos dados não reanimar as barras.
  const serie = useMemo(
    () =>
      horas
        ? horas.map(p => ({
            inicio: '',
            periodo: p.hora,
            Entradas: Number(p.entradas),
            Saídas: Number(p.saidas),
          }))
        : lancamentos
        ? lancamentos.map((l, i) => ({
            inicio: '',
            periodo: `${i + 1}. ${l.descricao}`,
            Entradas: Number(l.entrada),
            Saídas: Number(l.saida),
          }))
        : (dados?.rows ?? []).map(p => ({
            // Sai no clique: o rótulo do eixo não diz o ano nem o dia exato da
            // semana.
            inicio: p.periodo,
            periodo: rotuloEixo(p.periodo, agrupamento),
            Entradas: Number(p.entradas),
            Saídas: Number(p.saidas),
          })),
    [horas, lancamentos, dados, agrupamento],
  );
  const entradas = serie.reduce((s, p) => s + p.Entradas, 0);
  const saidas = serie.reduce((s, p) => s + p.Saídas, 0);
  // No dia único já não há o que recortar; com um período só, também não.
  const clicavel = !horas && !lancamentos && serie.length > 1;

  return (
    <CargaGrafico
      atualizando={atualizando}
      titulo={TITULO}
      isLoading={isLoading}
      isError={isError}
      error={error}
      onRetry={onRetry}
      h={ALTURA}
    >
      <Chart
        h={ALTURA}
        title={
          horas
            ? `${TITULO} (por hora do dia)`
            : lancamentos
            ? `${TITULO} (lançamentos do dia)`
            : TITULO
        }
        data={serie}
        format="bar"
        formatosPermitidos={['bar', 'line']}
        graphColor={[cores.entrada, cores.saida]}
        dataKeys={['Entradas', 'Saídas']}
        nameKey="periodo"
        isMoneyValue
        toolTipMsg={clicavel ? 'Clique para filtrar este período' : undefined}
        onItemClick={
          clicavel
            ? (ponto?: { inicio: string }) => {
                if (ponto) onSelecionarPeriodo(ponto.inicio, agrupamento);
              }
            : undefined
        }
        rightInfo={
          <Flex flexDir="column" w="30%" gridGap="2" ml="4">
            <ChartInfo
              titulo="Total Entradas"
              valor={formatValue(entradas)}
              corValor={cores.entrada}
              icon={<TrendingUp size={20} color={cores.entrada} />}
            />
            <ChartInfo
              titulo="Total Saídas"
              valor={formatValue(saidas)}
              corValor={cores.saida}
              icon={<TrendingDown size={20} color={cores.saida} />}
            />
            <ChartInfo
              titulo="Resultado"
              valor={formatValue(entradas - saidas)}
              corValor={cores.saldo}
              icon={<LineChart size={20} color={cores.saldo} />}
            />
          </Flex>
        }
      />
    </CargaGrafico>
  );
};

export default EntradasSaidasChart;
